import React, { useMemo, useCallback } from 'react';
import { Box, Checkbox, Chip } from '@mui/material';

import { Member, MemberTableProps } from '../../types/member.types';

import BaseDataTable from '@/components/table/base-data-table';
import { ColumnConfig } from '@/components/types/types';
import { convertDateTimeFormat } from '@/utils/date-time';
import { getCellStyle } from '@/components/styles/table-styles';
const MemberTable: React.FC<MemberTableProps> = ({
  onMemberClick,
  data = [],
  loading = false,
  currentPage = 1,
  itemsPerPage = 100,
  totalItems = 0,
  onPageChange,
  onItemsPerPageChange,
  selectedMembers = [],
  onSelectionChange: _onSelectionChange,
  filterStatus,
  sortField = null,
  sortOrder = 'desc',
  onSortChange,
}) => {
  // No need for client-side sorting anymore, use data as-is from API
  const tableData = useMemo(() => {
    return data || [];
  }, [data]);

  const visibleIds = useMemo(() => tableData.map((member) => member.id), [tableData]);

  const selectedOnPageCount = useMemo(
    () => visibleIds.filter((id) => selectedMembers.includes(id)).length,
    [visibleIds, selectedMembers]
  );

  // Check if all rows on current page are selected
  const allSelected = useMemo(
    () => tableData.length > 0 && selectedOnPageCount === tableData.length,
    [tableData, selectedOnPageCount]
  );

  // Check if some (but not all) rows on current page are selected
  const someSelected = useMemo(
    () => selectedOnPageCount > 0 && selectedOnPageCount < tableData.length,
    [selectedOnPageCount, tableData]
  );

  // Handle select all checkbox
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      // If in indeterminate state (some selected), deselect current page rows.
      if (someSelected) {
        const newSelections = selectedMembers.filter((id) => !visibleIds.includes(id));
        _onSelectionChange?.(newSelections);
        return;
      }

      if (checked) {
        // Select all rows on current page and keep selections from other pages.
        const newSelections = [...selectedMembers];
        visibleIds.forEach((id) => {
          if (!newSelections.includes(id)) {
            newSelections.push(id);
          }
        });
        _onSelectionChange?.(newSelections);
      } else {
        // Deselect only rows on current page.
        const newSelections = selectedMembers.filter((id) => !visibleIds.includes(id));
        _onSelectionChange?.(newSelections);
      }
    },
    [someSelected, selectedMembers, visibleIds, _onSelectionChange]
  );

  // Handle individual checkbox change
  const handleCheckboxChange = useCallback(
    (member: Member, checked: boolean) => {
      if (checked) {
        if (!selectedMembers.includes(member.id)) {
          _onSelectionChange?.([...selectedMembers, member.id]);
        }
        return;
      } else {
        _onSelectionChange?.(selectedMembers.filter((id) => id !== member.id));
      }
    },
    [selectedMembers, _onSelectionChange]
  );
  // Define column configuration for the member table
  const columns: ColumnConfig<Member>[] = useMemo(() => {
    const allColumns: ColumnConfig<Member>[] = [
      {
        key: 'checkbox',
        label: '',
        sortable: false,
        width: { xs: '5%', sm: '5%', md: '5%' },
        renderHeader: () => (
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        ),
        render: (_, member: Member) => (
          <Checkbox
            checked={selectedMembers.includes(member.id)}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(member, e.target.checked);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        ),
      },
      {
        key: 'responsiblePersonDisplay',
        label: '表示状態',
        sortable: false,
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (_, member: Member) => (
          <Box sx={getCellStyle('100%')} title={member.isSelectable ? '表示' : '非表示'}>
            {member.isSelectable ? '表示' : '非表示'}
          </Box>
        ),
      },
      {
        key: 'name',
        label: '氏名',
        sortable: false,
        width: { xs: '20%', sm: '20%', md: '20%' },
        render: (_, member: Member) => (
          <Box sx={getCellStyle('100%')} title={`${member.lastName} ${member.firstName}`}>
            {member.status === '削除済' && (
              <Chip
                label='削除済'
                size='small'
                sx={{
                  backgroundColor: '#FFE5E5',
                  color: '#D32F2F',
                  fontSize: '12px',
                  height: '20px',
                  fontWeight: 500,
                  border: '1px solid #FFCDD2',
                  flexShrink: 0,
                  '& .MuiChip-label': {
                    padding: '0 8px',
                  },
                }}
              />
            )}
            <Box
              component='span'
              sx={{
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.4,
                flex: 1,
                minWidth: 0, // Allow flex item to shrink below content size
              }}
            >
              {`${member.lastName} ${member.firstName}`}
            </Box>
          </Box>
        ),
      },
      {
        key: 'role',
        label: '権限',
        sortable: false,
        width: { xs: '15%', sm: '15%', md: '15%' },
        render: (value: string) => (
          <Box sx={getCellStyle('100%')} title={value || ''}>
            {value}
          </Box>
        ),
      },
      {
        key: 'department',
        label: '所属部署',
        sortable: false,
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (value: string) => (
          <Box sx={getCellStyle('100%')} title={value || ''}>
            {value}
          </Box>
        ),
      },
      {
        key: 'relatedDepartments',
        label: '関連部署',
        sortable: false,
        width: { xs: '20%', sm: '20%', md: '20%' },
        render: (value: string[]) => {
          const relatedDepts = value && value.length > 0 ? value.join(' / ') : '-';
          return (
            <Box sx={getCellStyle('100%')} title={relatedDepts}>
              {relatedDepts}
            </Box>
          );
        },
      },
      {
        key: 'updatedAt',
        label: '更新日時',
        sortable: true,
        sortField: 'updatedAt',
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (value: string) => (
          <Box sx={getCellStyle('100%')} title={convertDateTimeFormat(value)}>
            {convertDateTimeFormat(value)}
          </Box>
        ),
      },
      {
        key: 'createdAt',
        label: '作成日時',
        sortable: true,
        sortField: 'createdAt',
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (value: string) => (
          <Box sx={getCellStyle('100%')} title={convertDateTimeFormat(value)}>
            {convertDateTimeFormat(value)}
          </Box>
        ),
      },
    ];
    // Filter out checkbox column when filtering by 削除済 status
    if (filterStatus === '削除済') {
      return allColumns.filter((col) => col.key !== 'checkbox');
    }
    return allColumns;
  }, [
    selectedMembers,
    handleCheckboxChange,
    handleSelectAll,
    allSelected,
    someSelected,
    filterStatus,
  ]);
  // Get row style for selected members
  const getRowStyle = useCallback(
    (member: Member) => {
      // Change background color for selected rows
      if (selectedMembers.includes(member.id)) {
        return {
          backgroundColor: '#D7E2FF', // Primary90 - light primary background
        };
      }
      return {};
    },
    [selectedMembers]
  );

  return (
    <BaseDataTable<Member>
      data={tableData}
      loading={loading}
      onRowClick={onMemberClick}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      sortField={sortField}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      columns={columns}
      enableServerSort={true}
      hidePagination={true}
      getRowStyle={getRowStyle}
      wrapperStyles={{}}
    />
  );
};
export default MemberTable;
